import { Button, Form, notification, Progress, Row, Space, Table, theme, Transfer } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useGetCustomersQuery, useGetEmployeeQuery, useGetEmployeesQuery, useGetInsuranceQuery, useGetInsurancesQuery, useGetWorkTimes2Query } from "../../../../api/plamo-api";
import dayjs from "dayjs";
import FormItem from "../../form/form-item";
import { IconCashRegisterSolid } from "../../../../icon";
import { Link } from "react-router-dom";

const calcWorkTimesSum = (wts: any[]): string => {
  const sum = wts.map(wt => (dayjs(wt.endTime).valueOf() - dayjs(wt.startTime).valueOf()) / 1000 / 60).reduce((p: number, c: number) => p + c, 0);
  return ("00" + (sum / 60).toFixed(2).replace('.', ',')).substr(-5);
}

// Customize Table Transfer
const TableTransfer = (props) => {
  const { leftColumns, rightColumns, leftExtraButtonCaption, leftExtraButtonAlignment, leftExtraButtonAction, leftExtraButtonDisabled, 
    rightExtraButtonCaption, rightExtraButtonAlignment, rightExtraButtonAction, rightExtraButtonDisabled, ...restProps } = props;
  return (
    <Transfer style={{ width: '100%' }} {...restProps}>
      {({
        direction,
        filteredItems,
        onItemSelect,
        onItemSelectAll,
        selectedKeys: listSelectedKeys,
        disabled: listDisabled,
      }) => {
        const columns = direction === 'left' ? leftColumns : rightColumns;
        const rowSelection = {
          getCheckboxProps: () => ({ disabled: listDisabled }),
          onChange(selectedRowKeys) {
            onItemSelectAll(selectedRowKeys, 'replace');
          },
          selectedRowKeys: listSelectedKeys,
          selections: [Table.SELECTION_ALL, Table.SELECTION_INVERT, Table.SELECTION_NONE],
        };

        return (
          <>
          <Table
            rowSelection={rowSelection}
            columns={columns}
            dataSource={filteredItems}
            size="small"
            style={{ pointerEvents: listDisabled ? 'none' : undefined }}
            onRow={({ key, disabled: itemDisabled }) => ({
              onClick: () => {
                if (itemDisabled || listDisabled) {
                  return;
                }
                onItemSelect(key || '', !listSelectedKeys.includes(key || ''));
              },
            })}
          />
          <div style={{ textAlign: direction === 'left' ? leftExtraButtonAlignment : rightExtraButtonAlignment, padding: '1em' }}>
          <Button onClick={direction === 'left' ? leftExtraButtonAction : rightExtraButtonAction} disabled={direction === 'left' ? leftExtraButtonDisabled : rightExtraButtonDisabled}>{ direction === 'left' ? leftExtraButtonCaption : rightExtraButtonCaption }</Button>
          </div>
          </>
        );
      }}
    </Transfer>
  );
};

const filterOption = (input: string, item) =>
  item.name?.toLocaleLowerCase().includes(input.toLocaleLowerCase());

const CustomerDataLoader = ({customerId, selectedMonth, begin, end, setWorkTimesSum, setLoading}) => {
  const { data: workTimes } = useGetWorkTimes2Query({ f: JSON.stringify({ 
      "customerId": { c: "EQ", v: customerId },
      "startTime": { 
        c: "BTW", 
        v: dayjs(begin).utc().format('YYYY-MM-DDTHH:mm:ss') + '.000', 
        v2: dayjs(end).utc().format('YYYY-MM-DDTHH:mm:ss') + '.000', 
        t: 'DATE' 
      }
    }), st: JSON.stringify({ "id": "asc" }) }, { skip: !selectedMonth }) as any;

  useEffect(() => {
    if(workTimes?.items) {
      const sum = calcWorkTimesSum(workTimes?.items)
      setWorkTimesSum(sum)
    }
  }, [workTimes?.items, setWorkTimesSum])

  return <></>
}

const InsuranceColumn = ({ insuranceId }) => {
  const { data: insurance } = useGetInsuranceQuery({ id: insuranceId }) as any;
  return <>{ insurance?.name }</>
}

const EmployeesColumnEntry = ({ employeeId }) => {
  const { data: employee } = useGetEmployeeQuery({ id: employeeId }) as any;
  return <>{ employee?.firstName} { employee?.lastName }</>
}

const EmployeesColumn = ({ employeeIds }) => {
  return <>{ employeeIds.map((employeeId, idx) => <Link to={`/employee/${employeeId}`}><EmployeesColumnEntry employeeId={employeeId} /></Link>) }</>
}

const intersection = (arr1, arr2) => {
    const set2 = new Set(arr2);
    const res = arr1.filter(item => set2.has(item));
    return res;
}

const JOB_POLL_INTERVAL_MS = 3000;
const JOB_STORAGE_KEY = 'plamo.invoiceBatchJobId';

const isJobRunning = (status?: string) => status === 'CREATED' || status === 'RUNNING';

const readStoredJobId = (): string | null => {
  try {
    return localStorage.getItem(JOB_STORAGE_KEY);
  } catch (e) {
    return null;
  }
}

const storeJobId = (jobId: number | null) => {
  try {
    if (jobId) {
      localStorage.setItem(JOB_STORAGE_KEY, `${jobId}`);
    } else {
      localStorage.removeItem(JOB_STORAGE_KEY);
    }
  } catch (e) {
    // Storage unavailable, the job just can't be resumed after leaving the page
  }
}

function SettlementCustomerInvoices({ filter, setFilter }) {
  const userToken = useSelector(state => (state as any).user?.userToken);
  const [targetKeys, setTargetKeys] = useState<any>([]);
  const { data: customers } = useGetCustomersQuery({ st: JSON.stringify({ lastName: 'asc', firstName: 'asc' })}) as any;
  const [customersWithData, setCustomersWithData] = useState<any>([])
  const [batchJob, setBatchJob] = useState<any>(null);
  const [startingJob, setStartingJob] = useState<boolean>(false);
  const [cancellingJob, setCancellingJob] = useState<boolean>(false);
  const [downloadingPdf, setDownloadingPdf] = useState<boolean>(false);
  const [form] = Form.useForm()

  const selectedMonth = useMemo(() => filter.month, [filter])
  const begin = useMemo(() => dayjs(selectedMonth).date(1).hour(0).minute(0).second(0).millisecond(0),  [selectedMonth])
  const end = useMemo(() => dayjs(begin).add(1, 'month'),  [begin])
  
  const { data: workTimes } = useGetWorkTimes2Query({ f: JSON.stringify({ 
      "startTime": { 
        c: "BTW", 
        v: dayjs(begin).utc().format('YYYY-MM-DDTHH:mm:ss') + '.000', 
        v2: dayjs(end).utc().format('YYYY-MM-DDTHH:mm:ss') + '.000', 
        t: 'DATE' 
      }
    }), st: JSON.stringify({ "id": "asc" }) }, { skip: !selectedMonth }) as any;

  const workTimesFiltered = useMemo(() => workTimes?.items?.filter(wt => {
    return !filter.budgets?.length || filter.budgets.includes(wt.budget)
  }).filter(wt => !filter.invoiced || (filter.invoiced === 'INVOICED' && wt.inInvoice) || (filter.invoiced === 'NOT_INVOICED' && !wt.inInvoice)), [workTimes, filter.budgets, filter.invoiced]);

  useEffect(() => {
    console.log("Filter changed")
    if(customers?.items && workTimesFiltered) {
      const filterEmployeeIds = filter?.employeeIds;
      console.log("Filter employee ids", filterEmployeeIds)
      //setCustomersWithData(customers?.items.map(c => ({ key: c.id, name: `${c.firstName} ${c.lastName}`})))
      let customersWithData = customers?.items
          .filter(c => !filter?.insuranceIds?.length || filter.insuranceIds.includes(c.insuranceId))
          .map(c => ({ key: c.id, id: c.id, name: `${c.firstName} ${c.lastName}`, sum: 0, insuranceId: c.insuranceId, employeeIds: [] }))
      const customersWithDataById = {}
      customersWithData.forEach(c => customersWithDataById[c.id] = c)

      workTimesFiltered.forEach(wt => {
        if(wt.inInvoice) {
          console.log("Worktime in invoice", wt.id, wt.customerId);
        }
        if(customersWithDataById[wt.customerId]) {
          customersWithDataById[wt.customerId].sum += (dayjs(wt.endTime).valueOf() - dayjs(wt.startTime).valueOf()) / 1000 / 60;
          if(!customersWithDataById[wt.customerId].employeeIds.includes(wt.employeeId)) {
            customersWithDataById[wt.customerId].employeeIds.push(wt.employeeId)
          }
        }
        if(customersWithDataById[wt.customerId]) {
          if(customersWithDataById[wt.customerId].invoiced === undefined) {
            customersWithDataById[wt.customerId].invoiced = wt.inInvoice ? "INVOICED" : "NOT_INVOICED";
          } else {
            if((customersWithDataById[wt.customerId].invoiced === "NOT_INVOICED" && wt.inInvoice)
              || (customersWithDataById[wt.customerId].invoiced === "INVOICED" && !wt.inInvoice)
            ) {
              customersWithDataById[wt.customerId].invoiced = "PARTIALLY_INVOICED"
            }
          }
        }
      })

      customersWithData = customersWithData.filter(c => !filter?.employeeIds?.length || intersection(filter.employeeIds, c.employeeIds).length > 0)

      setCustomersWithData(customersWithData.filter(c => c.sum))
    }
  }, [customers?.items, workTimesFiltered, setCustomersWithData, filter])

  const columns = useMemo(() => [
    {
      dataIndex: "invoiced",
      title: "Abger.",
      render: (invoiced) => invoiced === "NOT_INVOICED" ? <></> : <span style={{ opacity: invoiced === 'INVOICED' ? 1 : 0.5 }} ><IconCashRegisterSolid /></span>,
    },
    {
      dataIndex: 'name',
      title: 'Name',
      render: (name, record) => <Link to={`/customer/${record.key}`}>{ name }</Link>
    },
    {
      dataIndex: 'sum',
      title: 'Stunden',
      render: (sum) => 
        ("00" + (sum / 60).toFixed(2).replace('.', ',')).substr(-5)
      ,
    },
    {
      dataIndex: 'insuranceId',
      title: 'Krankenkasse',
      render: (insuranceId) => <InsuranceColumn insuranceId={insuranceId} />
    },
    {
      dataIndex: 'employeeIds',
      title: 'Mitarbeiter',
      render: (employeeIds) => <EmployeesColumn employeeIds={employeeIds} />
    }
  ], [])

  const onChange = (nextTargetKeys) => {
    setTargetKeys(nextTargetKeys);
  };

  const onSelectAll = () => {
    setTargetKeys(customersWithData.map(c => c.key))
  }

  const onDeselectAll = () => {
    setTargetKeys([])
  }
  
  const downloadPdf = useCallback(async (jobId: number) => {
    setDownloadingPdf(true);
    try {
      const response = await fetch(`/api/plamo/invoice/generate/${jobId}`, { headers: { 'Authorization': `Bearer ${userToken}` } });
      if (response.status === 409) {
        notification.info({ message: "Die Rechnungserstellung läuft noch." });
        return;
      }
      if (!response.ok) {
        notification.error({ message: "Download fehlgeschlagen", description: `Status ${response.status}` });
        return;
      }
      const blob = await response.blob()
      if (response.status === 204 || blob.size === 0) {
        notification.info({ message: "Keine Arbeitszeiten gefunden, es wurden keine Rechnungen erstellt." });
        return;
      }
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a") as any;
      document.body.appendChild(a);
      a.style = "display: none";
      a.href = url;
      a.download = "Rechnung m. Leistungsnachweis.pdf";
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } finally {
      setDownloadingPdf(false);
    }
  }, [userToken])

  const generatePdf = useCallback(async () => {
    setStartingJob(true);
    try {
      const budgets = "budget=" + (filter.budgets?.length ? filter.budgets.join(',') : "supportAmount,absenceCare,conversion")
      const customerIds = "customerId=" + targetKeys.map(k => `${k}`).join(',')
      const response = await fetch(`/api/plamo/invoice/generate?${customerIds}&${budgets}&begin=${dayjs(begin).utc().format('YYYY-MM-DDTHH:mm:ss') + '.000'}&end=${dayjs(end).utc().format('YYYY-MM-DDTHH:mm:ss') + '.000'}`, { method: 'POST', headers: { 'Authorization': `Bearer ${userToken}` } });
      if (!response.ok) {
        notification.error({ message: "Rechnungserstellung konnte nicht gestartet werden", description: `Status ${response.status}` });
        return;
      }
      const job = await response.json();
      storeJobId(job.id);
      setBatchJob(job);
    } finally {
      setStartingJob(false);
    }
  }, [userToken, begin, end, targetKeys, filter.budgets])

  const cancelJob = useCallback(async () => {
    if (!batchJob) return;
    setCancellingJob(true);
    try {
      const response = await fetch(`/api/plamo/batch-job/${batchJob.id}/cancel`, { method: 'POST', headers: { 'Authorization': `Bearer ${userToken}` } });
      if (response.ok) {
        setBatchJob(await response.json());
      } else if (response.status !== 409) {
        // 409: job already finished, the next poll picks up the final status
        notification.error({ message: "Abbrechen fehlgeschlagen", description: `Status ${response.status}` });
      }
    } finally {
      setCancellingJob(false);
    }
  }, [userToken, batchJob])

  const closeJob = useCallback(() => {
    storeJobId(null);
    setBatchJob(null);
  }, [])

  // Resume a job that was started before the page was left
  useEffect(() => {
    const jobId = readStoredJobId();
    if (!jobId || !userToken) return;
    fetch(`/api/plamo/batch-job/${jobId}`, { headers: { 'Authorization': `Bearer ${userToken}` } })
      .then(response => response.ok ? response.json() : Promise.reject(response.status))
      .then(job => setBatchJob(job))
      .catch(() => storeJobId(null));
  }, [userToken])

  // Poll the job status while it is running
  const batchJobId = batchJob?.id;
  const batchJobRunning = isJobRunning(batchJob?.status);
  useEffect(() => {
    if (!batchJobId || !batchJobRunning) return;
    const interval = setInterval(async () => {
      try {
        const response = await fetch(`/api/plamo/batch-job/${batchJobId}`, { headers: { 'Authorization': `Bearer ${userToken}` } });
        if (!response.ok) return;
        const job = await response.json();
        setBatchJob(job);
        if (job.status === 'COMPLETED') {
          downloadPdf(job.id);
        }
      } catch (e) {
        // Network hiccup, retry on the next tick
      }
    }, JOB_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [batchJobId, batchJobRunning, userToken, downloadPdf])

  const jobTotalSteps = batchJob ? (batchJob.step ?? 0) + (batchJob.remainingSteps ?? 0) : 0;
  const jobProgress = jobTotalSteps ? Math.floor((batchJob.step ?? 0) / jobTotalSteps * 100) : 0;

  const { token } = theme.useToken();

  const formStyle: React.CSSProperties = {
    maxWidth: 'none',
    background: token.colorFillAlter,
    borderRadius: token.borderRadiusLG,
    padding: 24,
  };

  const onFieldsChange = useCallback((_, allFields) => {
    const values = form.getFieldsValue()
    if(JSON.stringify(values) !== JSON.stringify(filter)) {
      setFilter(values)
    }
    console.log("Fields changed", values)
  }, [form, filter, setFilter])

  useEffect(() => {
    form.setFieldsValue(filter);
  }, [form, filter])

  return (<>
    <Form form={form} style={formStyle} onFieldsChange={onFieldsChange}>
      <Row><h3>Filter</h3></Row>
      <Row gutter={24}>
        <FormItem fieldName="month" label="Monat" type="month" />
        <FormItem fieldName="employeeIds" label="Mitarbeiter" type="ref" typeData={{ query: useGetEmployeesQuery, getId: employee => employee.id, render: employee => `${employee.firstName} ${employee.lastName}`, multiple: true }} />
        <FormItem fieldName="insuranceIds" label="Krankenkasse(n)" type="ref" typeData={{ query: useGetInsurancesQuery, getId: insurance => insurance.id, render: insurance => insurance.name, multiple: true }} />
        <FormItem fieldName="budgets" label="Budgets" type="enum" typeData={{ options: [{ value: 'supportAmount', label: "EB" }, { value: 'absenceCare', label: "VHP" }, { value: "conversion", label: "U" }], multiple: true }} /><FormItem fieldName="invoiced" label="Abgerechnet" type="enum" typeData={{ options: [{ value: 'NOT_INVOICED', label: "Nicht abgerechnet" }, { value: 'INVOICED', label: "Abgerechnet" }, /* { value: "PARTIALLY_INVOICED", label: "Teilweise abgerechnet" } */], multiple: false }} />
      </Row>
    </Form>

    <TableTransfer
        style={{ marginTop: '2em' }}
        dataSource={customersWithData}
        targetKeys={targetKeys}
        showSearch
        showSelectAll={false}
        onChange={onChange}
        filterOption={filterOption}
        leftColumns={columns}
        rightColumns={columns}
        leftExtraButtonAction={onSelectAll}
        leftExtraButtonAlignment="right"
        leftExtraButtonCaption="Alle auswählen >>"
        leftExtraButtonDisabled={customersWithData?.length === targetKeys.length}
        rightExtraButtonAction={onDeselectAll}
        rightExtraButtonAlignment="left"
        rightExtraButtonCaption="<< Alle entfernen"
        rightExtraButtonDisabled={targetKeys.length === 0}
      />

    { selectedMonth && 
    <>
      { batchJob &&
      <div style={{ marginTop: '2em' }}>
        <Progress
          percent={jobProgress}
          status={batchJob.status === 'CANCELLED' ? 'exception' : batchJob.status === 'COMPLETED' ? 'success' : 'active'}
          format={() => `${batchJob.step ?? 0} / ${jobTotalSteps}`}
        />
        <div style={{ textAlign: 'right', marginTop: '0.5em' }}>
          <Space size="small">
            <span>
              { batchJob.status === 'CREATED' && 'Rechnungserstellung wartet auf Start…' }
              { batchJob.status === 'RUNNING' && 'Rechnungen werden erstellt…' }
              { batchJob.status === 'COMPLETED' && 'Rechnungserstellung abgeschlossen.' }
              { batchJob.status === 'CANCELLED' && 'Rechnungserstellung abgebrochen. Bereits erstellte Rechnungen bleiben erhalten.' }
            </span>
            { isJobRunning(batchJob.status) &&
              <Button danger loading={cancellingJob} onClick={cancelJob}>Abbrechen</Button> }
            { batchJob.status === 'COMPLETED' &&
              <Button loading={downloadingPdf} onClick={() => downloadPdf(batchJob.id)}>Erneut herunterladen</Button> }
            { batchJob.status === 'CANCELLED' &&
              <Button loading={downloadingPdf} onClick={() => downloadPdf(batchJob.id)}>Teilergebnis herunterladen</Button> }
            { !isJobRunning(batchJob.status) &&
              <Button onClick={closeJob}>Schließen</Button> }
          </Space>
        </div>
      </div> }

      <div style={{ textAlign: 'right', marginTop: '2em' }}>
        <Space size="small">
          <Button type="primary" loading={startingJob} disabled={!targetKeys.length || isJobRunning(batchJob?.status)} onClick={() => generatePdf()}>
            Rechnungen erstellen
          </Button>
        </Space>
      </div>

    </>}

    {
      false && customers?.items?.map(c => <CustomerDataLoader customerId={c.id} begin={begin} end={end} setWorkTimesSum={() => {}} setLoading={() => {}} selectedMonth={selectedMonth} />)
    }
  </>)
}

export default SettlementCustomerInvoices;